import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, Star, Flame } from 'lucide-react';
import {
  tacosMenu as defaultTacos,
  burgers as defaultBurgers,
  sharingMenu as defaultSharing,
  kidsMenu as defaultKids,
  desserts as defaultDesserts,
  drinks as defaultDrinks,
  lunchDeal as defaultLunchDeal
} from '../data/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PopularBadge = () => (
  <span className="inline-flex items-center space-x-1 bg-[#D4AF37] text-black px-2 py-0.5 rounded-full text-xs font-bold ml-2">
    <Flame size={12} />
    <span>POPULAIRE</span>
  </span>
);

const MenuSection = () => {
  const [menuData, setMenuData] = useState(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/menu`);
        setMenuData(res.data);
      } catch {
        // fallback to mockData
      }
    };
    fetchMenu();
  }, []);

  const tacosMenu = menuData?.tacos || defaultTacos;
  const burgers = menuData?.burgers || defaultBurgers;
  const sharingMenu = menuData?.sharing || defaultSharing;
  const kidsMenu = menuData?.kids || defaultKids;
  const desserts = menuData?.desserts || defaultDesserts;
  const drinks = menuData?.drinks || defaultDrinks;
  const lunchDeal = menuData?.lunchDeal || defaultLunchDeal;
  return (
    <section id="menu" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl lg:text-6xl font-bold text-black mb-4"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Notre <span className="text-[#D4AF37]">Menu</span>
          </h2>
          <p className="text-xl text-black/70 max-w-2xl mx-auto">
            Découvrez nos spécialités : tacos, roulés, burgers et plus encore
          </p>
        </div>

        {/* Lunch Deal Banner */}
        <div className="mb-12 bg-black text-white rounded-2xl p-8 shadow-2xl transform hover:scale-105 transition-transform duration-300 relative overflow-hidden">
          {/* Best Seller Ribbon */}
          <div className="absolute top-3 right-3 bg-[#D4AF37] text-black px-4 py-1 rounded-full font-bold text-sm flex items-center space-x-1">
            <Star size={14} className="fill-black" />
            <span>N°1 DES VENTES</span>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <span className="inline-block bg-[#D4AF37] text-black px-4 py-2 rounded-full text-sm font-bold mb-2">
                {lunchDeal.badge}
              </span>
              <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                {lunchDeal.name}
              </h3>
              <p className="text-white/90">{lunchDeal.description}</p>
            </div>
            <div className="text-center mt-4 md:mt-0">
              <div className="text-5xl font-bold text-[#D4AF37]">{lunchDeal.price.toFixed(2)}€</div>
              <a
                href={`tel:04 87 66 22 41`}
                className="inline-block mt-4 bg-[#D4AF37] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#C5A028] transition-colors"
              >
                Appeler pour commander
              </a>
            </div>
          </div>
        </div>

        {/* Menu Accordion */}
        <div className="space-y-6">
          {/* Tacos/Roulés */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="tacos" className="border-none">
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-white/50 transition-colors">
                  <div className="flex items-center justify-between w-full">
                    <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                      {tacosMenu.title}
                    </h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <p className="text-black/70 mb-6 italic">{tacosMenu.description}</p>

                  {/* Formats */}
                  <div className="mb-6">
                    <h4 className="font-bold text-lg mb-3 text-black">Formats</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      {tacosMenu.formats.map((format, index) => (
                        <div
                          key={index}
                          className={`bg-[#ECEC75] p-4 rounded-xl border-2 transition-colors relative ${
                            format.isPopular ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/30' : 'border-black/10 hover:border-[#D4AF37]'
                          }`}
                        >
                          {format.isPopular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                              <span className="inline-flex items-center space-x-1 bg-[#D4AF37] text-black px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                                <Flame size={10} />
                                <span>POPULAIRE</span>
                              </span>
                            </div>
                          )}
                          <div className="font-bold text-black">{format.name}</div>
                          <div className="text-sm text-black/70">{format.description}</div>
                          <div className="text-xl font-bold text-[#D4AF37] mt-2">{format.price.toFixed(2)}€</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-black/60 mt-3">
                      Option Menu : +{tacosMenu.menuOption.toFixed(2)}€
                    </p>
                  </div>

                  {/* Viandes */}
                  <div className="mb-6">
                    <h4 className="font-bold text-lg mb-3 text-black">Choix de viande</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {tacosMenu.meats.map((meat, index) => (
                        <div
                          key={index}
                          className="bg-white p-3 rounded-lg text-sm text-black border border-black/10"
                        >
                          {meat.name}
                          {meat.extra > 0 && (
                            <span className="text-[#D4AF37] ml-1">(+{meat.extra.toFixed(2)}€)</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sauces */}
                  <div className="mb-6">
                    <h4 className="font-bold text-lg mb-3 text-black">Sauces</h4>
                    <div className="flex flex-wrap gap-2">
                      {tacosMenu.sauces.map((sauce, index) => (
                        <span
                          key={index}
                          className="bg-black text-white px-3 py-1 rounded-full text-sm"
                        >
                          {sauce}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Suppléments */}
                  <div className="mb-6">
                    <h4 className="font-bold text-lg mb-3 text-black">
                      Suppléments (+{tacosMenu.supplementPrice.toFixed(2)}€)
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tacosMenu.supplements.map((sup, index) => (
                        <span
                          key={index}
                          className="bg-[#D4AF37]/20 text-black px-3 py-1 rounded-full text-sm border border-[#D4AF37]"
                        >
                          {sup}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Gratinage */}
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-black">Gratinage</h4>
                    <div className="grid md:grid-cols-4 gap-3 mb-3">
                      {tacosMenu.gratinage.map((grat, index) => (
                        <div key={index} className="bg-white p-3 rounded-lg border border-black/10">
                          <div className="text-sm font-semibold text-black">{grat.type}</div>
                          <div className="text-[#D4AF37] font-bold">+{grat.price.toFixed(2)}€</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tacosMenu.gratinageOptions.map((option, index) => (
                        <span
                          key={index}
                          className="bg-black/5 text-black px-3 py-1 rounded-full text-sm"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Burgers */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="burgers" className="border-none">
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-white/50 transition-colors">
                  <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                    Nos Burgers
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {burgers.map((burger, index) => (
                      <div
                        key={index}
                        className={`bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 relative ${
                          burger.isPopular ? 'ring-2 ring-[#D4AF37]' : ''
                        }`}
                      >
                        {burger.isPopular && (
                          <div className="absolute top-3 right-3 z-10">
                            <span className="inline-flex items-center space-x-1 bg-[#D4AF37] text-black px-2 py-1 rounded-full text-xs font-bold">
                              <Flame size={12} />
                              <span>POPULAIRE</span>
                            </span>
                          </div>
                        )}
                        <div className="h-48 overflow-hidden">
                          <img
                            src={burger.image}
                            alt={burger.name}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-lg text-black mb-1">{burger.name}</h4>
                          <p className="text-sm text-black/70 mb-4">{burger.description}</p>
                          <div className="flex gap-2">
                            <div className="flex-1 text-center bg-[#ECEC75] py-2 rounded-lg">
                              <div className="text-xs text-black/60">Seul</div>
                              <div className="font-bold text-black">{burger.priceSeul.toFixed(2)}€</div>
                            </div>
                            <div className="flex-1 text-center bg-black text-white py-2 rounded-lg">
                              <div className="text-xs text-white/80">Menu</div>
                              <div className="font-bold">{burger.priceMenu.toFixed(2)}€</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* À Partager */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="sharing" className="border-none">
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-white/50 transition-colors">
                  <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                    À Partager (ou pas)
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {sharingMenu.map((item, index) => (
                      <div
                        key={index}
                        className="bg-[#ECEC75] p-4 rounded-xl border-2 border-black/10 hover:border-[#D4AF37] transition-colors"
                      >
                        <div className="font-bold text-black mb-1">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-black/60 mb-2">{item.description}</div>
                        )}
                        <div className="text-xl font-bold text-[#D4AF37]">{item.price.toFixed(2)}€</div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Menu Enfant */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="kids" className="border-none">
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-white/50 transition-colors">
                  <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                    Menu Enfant
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <div className="bg-[#D4AF37]/20 p-6 rounded-xl border-2 border-[#D4AF37]">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <h4 className="font-bold text-xl text-black mb-2">{kidsMenu.name}</h4>
                        <p className="text-black/70">{kidsMenu.items}</p>
                      </div>
                      <div className="text-3xl font-bold text-[#D4AF37]">{kidsMenu.price.toFixed(2)}€</div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Desserts & Boissons */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="desserts" className="border-none">
                <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-white/50 transition-colors">
                  <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Crimson Text, serif' }}>
                    Desserts & Boissons
                  </h3>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Desserts */}
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-black">Desserts</h4>
                      <div className="space-y-3">
                        {desserts.map((dessert, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center bg-white p-3 rounded-lg border border-black/10"
                          >
                            <span className="text-black">{dessert.name}</span>
                            <span className="font-bold text-[#D4AF37]">{dessert.price.toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Boissons */}
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-black">Boissons</h4>
                      <div className="space-y-3">
                        {drinks.map((drink, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center bg-white p-3 rounded-lg border border-black/10"
                          >
                            <span className="text-black">{drink.name}</span>
                            <span className="font-bold text-[#D4AF37]">{drink.price.toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
